import type { PickemEvent } from '@api/generated/types.gen';

export type PickemEffectiveStatus =
  | 'draft'
  | 'scheduled'
  | 'active'
  | 'locked'
  | 'completed';

/**
 * Derive the user-facing status for a pickem event.
 * `draft` and `completed` are admin-set; the rest come from publishDate + lockDate.
 */
export function getEffectiveStatus(
  event: PickemEvent,
  now = new Date(),
): PickemEffectiveStatus {
  if (event.status === 'draft') return 'draft';
  if (event.status === 'completed') return 'completed';
  const publishDate = event.publishDate ? new Date(event.publishDate) : null;
  const lockDate = event.lockDate ? new Date(event.lockDate) : null;
  if (publishDate && now < publishDate) return 'scheduled';
  if (lockDate && now >= lockDate) return 'locked';
  return 'active';
}

export const effectiveStatusLabel: Record<PickemEffectiveStatus, string> = {
  draft: 'Draft',
  scheduled: 'Scheduled',
  active: 'Open',
  locked: 'Locked',
  completed: 'Completed',
};

export const effectiveStatusBadgeClass: Record<PickemEffectiveStatus, string> =
  {
    draft: 'bg-muted text-muted-foreground',
    scheduled: 'bg-sky-500/15 text-sky-400 border border-sky-500/30',
    active: 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30',
    locked: 'bg-amber-500/15 text-amber-400 border border-amber-500/30',
    completed: 'bg-blue-500/15 text-blue-400 border border-blue-500/30',
  };

/**
 * Human-friendly relative time to a target date: "in 3d", "in 4h 12m", "2h ago".
 */
export function formatRelativeTime(target: Date | null, now = new Date()): string | null {
  if (!target) return null;
  const diffMs = target.getTime() - now.getTime();
  const absMs = Math.abs(diffMs);
  const minutes = Math.floor(absMs / 60_000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  let label: string;
  if (days > 0) {
    const remainingHours = hours - days * 24;
    label = remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`;
  } else if (hours > 0) {
    const remainingMinutes = minutes - hours * 60;
    label = remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
  } else if (minutes > 0) {
    label = `${minutes}m`;
  } else {
    label = '<1m';
  }
  return diffMs >= 0 ? `in ${label}` : `${label} ago`;
}
